// Cross-content graph. Every song, teaching, activation, and book becomes a
// node. Related nodes are computed from (a) explicit refs the editor sets on a
// wisdom post and (b) derived tags from mood / category / frequency. No data
// entry required to get sensible recommendations — richer refs upgrade them
// automatically.

import { RELEASES, WISDOM, ACTIVATIONS, BOOKS, formatReleaseDate, type Release, type Activation, type WisdomPost } from "./content";

export type ItemKind = "music" | "wisdom" | "activation" | "book";

export interface EcosystemItem {
  kind: ItemKind;
  slug: string;
  title: string;
  subtitle: string;      // one-line description for cards / search
  href: string;          // canonical path
  hrefRoute: `/music/$slug` | `/wisdom/$slug` | `/activations/$slug` | `/books/$slug`;
  tags: string[];        // normalized, lowercase
  body?: string;         // additional searchable text (lyrics/instructions/body)
}

const norm = (s: string) => s.trim().toLowerCase();

function releaseItem(r: Release): EcosystemItem {
  const date = formatReleaseDate(r);
  return {
    kind: "music",
    slug: r.slug,
    title: r.title,
    subtitle: [r.type, date ?? r.frequency].filter(Boolean).join(" · "),
    href: `/music/${r.slug}`,
    hrefRoute: "/music/$slug",
    tags: [...r.mood, r.frequency, r.type].map(norm),
    body: [r.statement, ...(r.lyricsExcerpts ?? [])].join("\n"),
  };
}

function wisdomItem(w: WisdomPost): EcosystemItem {
  return {
    kind: "wisdom",
    slug: w.slug,
    title: w.title,
    subtitle: `${w.category} · ${w.readingTime}`,
    href: `/wisdom/${w.slug}`,
    hrefRoute: "/wisdom/$slug",
    tags: [w.category].map(norm),
    body: [w.excerpt, w.pullQuote, ...w.body].join("\n"),
  };
}

function activationItem(a: Activation): EcosystemItem {
  return {
    kind: "activation",
    slug: a.slug,
    title: a.title,
    subtitle: `${a.category} · ${a.duration}`,
    href: `/activations/${a.slug}`,
    hrefRoute: "/activations/$slug",
    tags: [a.category, a.breathPattern].map(norm),
    body: [a.intention, a.instruction, a.phrase, a.journal].join("\n"),
  };
}

function bookItem(b: (typeof BOOKS)[number]): EcosystemItem {
  const tags = [
    "book",
    ...(b.tags ?? []),
    ...(b.category ? b.category.split(/[·,/]/).map((s) => s.trim()).filter(Boolean) : []),
    ...(b.author ? [b.author] : []),
    ...(b.authorLegalName ? [b.authorLegalName] : []),
  ].map(norm);
  return {
    kind: "book",
    slug: b.slug,
    title: b.title,
    subtitle: b.subtitle,
    href: `/books/${b.slug}`,
    hrefRoute: "/books/$slug",
    tags,
    body: [b.longDescription, b.author, b.authorLegalName, b.category, (b.tags ?? []).join(" ")].filter(Boolean).join("\n"),
  };
}

/** Flattened index of every ecosystem node. */
export const ECOSYSTEM: EcosystemItem[] = [
  ...RELEASES.map(releaseItem),
  ...WISDOM.map(wisdomItem),
  ...ACTIVATIONS.map(activationItem),
  ...BOOKS.map(bookItem),
];

export function findItem(kind: ItemKind, slug: string): EcosystemItem | undefined {
  return ECOSYSTEM.find((i) => i.kind === kind && i.slug === slug);
}

/**
 * Explicit refs authored on a wisdom post — always ranked first.
 * Returns EcosystemItems in the order the editor listed them.
 */
function explicitRelated(kind: ItemKind, slug: string): EcosystemItem[] {
  const out: EcosystemItem[] = [];
  if (kind === "wisdom") {
    const w = WISDOM.find((x) => x.slug === slug);
    if (!w) return out;
    if (w.relatedMusic) { const i = findItem("music", w.relatedMusic); if (i) out.push(i); }
    if (w.relatedActivation) { const i = findItem("activation", w.relatedActivation); if (i) out.push(i); }
    if (w.relatedBook) { const i = findItem("book", w.relatedBook); if (i) out.push(i); }
    return out;
  }
  // For music / activation / book: pull in any wisdom post that references them.
  const backrefs = WISDOM.filter((w) =>
    (kind === "music" && w.relatedMusic === slug) ||
    (kind === "activation" && w.relatedActivation === slug) ||
    (kind === "book" && w.relatedBook === slug),
  );
  for (const w of backrefs) out.push(wisdomItem(w));
  return out;
}

/** Overlap score between two tag sets. */
function tagOverlap(a: string[], b: string[]): number {
  if (!a.length || !b.length) return 0;
  const set = new Set(a);
  let n = 0;
  for (const t of b) if (set.has(t)) n++;
  return n;
}

/**
 * Get up to `limit` related items across kinds, biased toward variety
 * (at least one of each other kind when available).
 */
export function getRelated(kind: ItemKind, slug: string, limit = 4): EcosystemItem[] {
  const origin = findItem(kind, slug);
  const seen = new Set<string>([`${kind}:${slug}`]);
  const out: EcosystemItem[] = [];

  // 1. Explicit refs first.
  for (const item of explicitRelated(kind, slug)) {
    const key = `${item.kind}:${item.slug}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= limit) return out;
  }

  if (!origin) return out;

  // 2. Score every other node by tag overlap; keep variety across kinds.
  const scored = ECOSYSTEM
    .filter((i) => !seen.has(`${i.kind}:${i.slug}`))
    .map((i) => ({ item: i, score: tagOverlap(origin.tags, i.tags) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  const usedKinds = new Set(out.map((o) => o.kind));
  // First pass: prefer kinds we haven't shown yet.
  for (const { item } of scored) {
    if (usedKinds.has(item.kind)) continue;
    out.push(item);
    usedKinds.add(item.kind);
    seen.add(`${item.kind}:${item.slug}`);
    if (out.length >= limit) return out;
  }
  // Second pass: fill remaining slots by pure score.
  for (const { item } of scored) {
    const key = `${item.kind}:${item.slug}`;
    if (seen.has(key)) continue;
    out.push(item);
    seen.add(key);
    if (out.length >= limit) return out;
  }
  return out;
}

export const KIND_LABEL: Record<ItemKind, string> = {
  music: "Listen",
  wisdom: "Read",
  activation: "Practice",
  book: "Study",
};

export const KIND_NOUN: Record<ItemKind, string> = {
  music: "Song",
  wisdom: "Teaching",
  activation: "Activation",
  book: "Book",
};
